/**
 * Hero variant 2 — "Vinyl".
 *
 * A record the size of the viewport, half off the right edge, turning under a
 * tonearm. The services are its tracks: the arm drops onto whichever one is
 * playing, the sleeve note changes with it, and the record can be grabbed and
 * spun by hand. It reads as a record sleeve because the firm's centre of
 * gravity is music — label-side and independent artist work.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import ElfHeroNav from './ElfHeroNav';
import useMagnetic from './useMagnetic';
import { useConsultOpener } from './elfConsult';
import elfLogo from '../../../../assets/elf/elfcircle-logo.png';
import { scrollPastHero } from './ElfTear';

interface Track {
  no: string;
  name: string;
  note: string;
}

const TRACKS: Track[] = [
  { no: '01', name: 'Marketing', note: 'Release campaigns, drop-day activation, press and playlist placement, and the media buy behind them.' },
  { no: '02', name: 'Web + GEO', note: 'Sites, stores and booking flows built in house — and tuned to show up inside AI answers, not just search.' },
  { no: '03', name: 'Events', note: 'A monthly networking circuit across the launch markets, and a 21-county festival of our own.' },
  { no: '04', name: 'Merch', note: 'Drops cut for the rooms they belong to, produced and fulfilled alongside the campaign.' },
  { no: '05', name: 'Consultations', note: 'A straight conversation first: what you are trying to move, and whether we are the right room for it.' },
];

/** Degrees a second the record turns when nobody is holding it. */
const SPIN_DEG_PER_S = 26;
/** Arm rest angle, and how far it swings per track inward. */
const ARM_REST = 6;
const ARM_STEP = 4.6;
const TRACK_MS = 4200;

export const ElfHeroVinyl: React.FC = () => {
  const discRef = useRef<HTMLDivElement>(null);
  const angleRef = useRef(0);
  const dragRef = useRef<{ id: number; last: number } | null>(null);
  const heldRef = useRef(false);
  const releaseRef = useRef<number>();

  const [at, setAt] = useState(0);
  const magnetic = useMagnetic();
  const bookConsult = useConsultOpener({ source: 'hero.vinyl', title: 'Book a consultation' });

  /* --- the record turns ---------------------------------------------------- */
  useEffect(() => {
    const disc = discRef.current;
    if (!disc) return;
    // Slower for reduced motion, never stopped: a still record looks broken.
    const pace = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0.4 : 1;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;
      // While a hand is on the record, the drag sets the angle instead.
      if (!dragRef.current) {
        angleRef.current = (angleRef.current + SPIN_DEG_PER_S * pace * dt) % 360;
        disc.style.transform = `rotate(${angleRef.current}deg)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  /* --- the arm moves down the tracks --------------------------------------- */
  useEffect(() => {
    const id = window.setInterval(() => {
      if (heldRef.current) return;
      setAt((i) => (i + 1) % TRACKS.length);
    }, TRACK_MS);
    return () => {
      window.clearInterval(id);
      window.clearTimeout(releaseRef.current);
    };
  }, []);

  const play = useCallback((i: number, sticky: boolean) => {
    window.clearTimeout(releaseRef.current);
    setAt(i);
    heldRef.current = true;
    if (sticky) {
      releaseRef.current = window.setTimeout(() => {
        heldRef.current = false;
      }, 9000);
    }
  }, []);

  const resume = useCallback(() => {
    window.clearTimeout(releaseRef.current);
    heldRef.current = false;
  }, []);

  /* --- spinning the record by hand ----------------------------------------- */
  const onDiscDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const disc = discRef.current;
    if (!disc) return;
    const r = disc.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    dragRef.current = { id: e.pointerId, last: Math.atan2(e.clientY - cy, e.clientX - cx) };
    disc.setPointerCapture(e.pointerId);
  }, []);

  const onDiscMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const disc = discRef.current;
    if (!drag || !disc || drag.id !== e.pointerId) return;
    const r = disc.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const now = Math.atan2(e.clientY - cy, e.clientX - cx);
    let delta = ((now - drag.last) * 180) / Math.PI;
    // Crossing the ±180° seam would otherwise throw the record a half-turn.
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    drag.last = now;
    angleRef.current += delta;
    disc.style.transform = `rotate(${angleRef.current}deg)`;
  }, []);

  const onDiscUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.id === e.pointerId) dragRef.current = null;
  }, []);

  const track = TRACKS[at];

  return (
    <div className="elf-hv elf-hv--vinyl">
      <ElfHeroNav />

      <div className="hvv">
        <div className="hvv__copy">
          <span className="hvv__side">
            Side A
            <i />
            The service hub
          </span>
          <h1 className="hvv__h1">
            <span className="mask">
              <span style={{ animationDelay: '.3s' }}>Where culture</span>
            </span>
            <span className="mask">
              <span style={{ animationDelay: '.42s' }}>
                lives on <em>repeat.</em>
              </span>
            </span>
          </h1>

          <div className="hvv__tracks" onPointerLeave={(e) => e.pointerType === 'mouse' && resume()}>
            {TRACKS.map((t, i) => (
              <button
                key={t.no}
                type="button"
                className="hvv__track"
                data-on={i === at ? 'true' : undefined}
                aria-pressed={i === at}
                onPointerEnter={(e) => e.pointerType === 'mouse' && play(i, false)}
                onClick={() => play(i, true)}
              >
                <span className="hvv__no">{t.no}</span>
                <span className="hvv__name">{t.name}</span>
                <span className="hvv__bars" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                  <i />
                </span>
              </button>
            ))}
          </div>

          <p className="hvv__note" key={track.no}>
            {track.note}
          </p>

          <div className="hvv__cta">
            <button type="button" className="cta mag" {...magnetic} onClick={bookConsult}>
              Book a Consultation
            </button>
            <button
              type="button"
              className="discover"
              onClick={scrollPastHero}
            >
              Discover <i>↗</i>
            </button>
          </div>
        </div>

        <div className="hvv__deck">
          <div
            ref={discRef}
            className="hvv__disc"
            onPointerDown={onDiscDown}
            onPointerMove={onDiscMove}
            onPointerUp={onDiscUp}
            onPointerCancel={onDiscUp}
            role="img"
            aria-label="Record — the services as tracks"
          >
            <span className="hvv__grooves" />
            <span className="hvv__label">
              <img src={elfLogo} alt="" />
              <b>ELF-CIRCLE</b>
              <i>33⅓ · since 1991</i>
            </span>
            <span className="hvv__spindle" />
          </div>

          <div className="hvv__arm" style={{ transform: `rotate(${ARM_REST + at * ARM_STEP}deg)` }}>
            <span className="hvv__armbase" />
            <span className="hvv__armrod" />
            <span className="hvv__armhead" />
          </div>

          <span className="hvv__playing">
            <b>Now playing</b>
            {track.no} · {track.name}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ElfHeroVinyl;
